// Byte amounts as a number plus a unit.
//
// Storage and rate-limit figures are stored as raw bytes, but a contractual
// value like "10 TB" is 10995116277760 — a number nobody can type or proof-read
// reliably, where a single misplaced digit sells ten times the agreed volume.
// These helpers convert between the stored bytes and an amount/unit pair so the
// operator works in the unit the contract is written in.
//
// 1024-based, matching formatBytes() so the entry field and every display of
// the same value agree.

export type ByteUnit = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

/** Ascending, so the largest fitting unit is found by searching backwards. */
export const BYTE_UNITS: ByteUnit[] = ['B', 'KB', 'MB', 'GB', 'TB'];

export const BYTE_FACTOR: Record<ByteUnit, number> = {
  B: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
  TB: 1024 ** 4,
};

/** Amount + unit -> bytes, as the string the form fields carry. An empty or
 *  unparsable amount yields '' so "cleared" stays distinguishable from 0. */
export function bytesFrom(amount: string, unit: ByteUnit): string {
  if (amount.trim() === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n * BYTE_FACTOR[unit]));
}

/** Decimals kept when showing an amount in a large unit. */
const MAX_DECIMALS = 3;

/**
 * Bytes -> the amount/unit pair that reads the way the contract is written.
 *
 * Preference is the LARGEST unit that still represents the value exactly within
 * a few decimals, so 1610612736 comes back as 1.5 GB rather than the equally
 * exact but unreadable 1536 MB, and 10995116277760 as 10 TB. A value that no
 * large unit can express exactly (say 1 GB + 1 byte) steps down to the largest
 * unit that divides it evenly, ending at plain bytes — better an awkward number
 * than a rounded one, since these figures are contractual.
 *
 * `fallback` is the unit used when there is nothing to derive from (0 or an
 * empty field) — storage fields default to GB so a typed "10" means 10 GB.
 */
export function splitBytes(bytes: number, fallback: ByteUnit = 'GB'): { amount: number; unit: ByteUnit } {
  if (!Number.isFinite(bytes) || bytes <= 0) return { amount: 0, unit: fallback };
  for (let i = BYTE_UNITS.length - 1; i >= 0; i--) {
    const u = BYTE_UNITS[i];
    if (bytes < BYTE_FACTOR[u]) continue;
    const amount = Number((bytes / BYTE_FACTOR[u]).toFixed(MAX_DECIMALS));
    if (amount * BYTE_FACTOR[u] === bytes) return { amount, unit: u };
  }
  return { amount: bytes, unit: 'B' };
}

/** The amount to prefill the input with, '' for an empty value. */
export function amountText(bytes: string, unit: ByteUnit): string {
  if (bytes.trim() === '') return '';
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '0';
  const amount = n / BYTE_FACTOR[unit];
  // Trim float noise from divisions like 1.5 GB without truncating real digits.
  return String(Number(amount.toFixed(6)));
}
